import React, { act, useState } from 'react'
import { useQuery, useMutation } from '@apollo/react-hooks'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert,
  Checkbox,
  Collapse,
} from '@mui/material'
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import { toast } from 'react-toastify'
import Loader from '../Loader'
import PageHeader from '../Common/PageHeader'

import {
  GET_WORKFLOWS,
  CREATE_WORKFLOW,
  UPDATE_WORKFLOW,
  DELETE_WORKFLOW,
} from '../../api/workflow'
import { GET_ROLES } from '../../api/role'
import { CREATE_STAGE, UPDATE_STAGE, DELETE_STAGE } from '../../api/stage'

interface Workflow {
  id: number
  name: string
  active: boolean
  approval_stages: ApprovalStage[]
}

interface ApprovalStage {
  id: number
  order_number: number
  is_payment_stage: boolean
  workflow: {
    id: number
    name: string
    active: boolean
  }
  approver_role:
    | {
        id: number
        name: string
        scope: string
      }
    | undefined
}

interface Role {
  id: number
  name: string
  scope: string
}

const Workflows: React.FC = () => {
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingWorkflow, setEditingWorkflow] = useState<Partial<Workflow>>({})
  const [isAdding, setIsAdding] = useState(false)
  const [expanded, setExpanded] = useState<number | null>(null)

  const [editingStageId, setEditingStageId] = useState<number | null>(null)
  const [editingStage, setEditingStage] = useState<Partial<ApprovalStage>>({})
  const [isAddingStage, setIsAddingStage] = useState(false)

  const [showSuccess, setShowSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const {
    data: workflowsData,
    loading: workflowsLoading,
    refetch,
  } = useQuery(GET_WORKFLOWS)
  const { data: rolesData, loading: rolesLoading } = useQuery(GET_ROLES)

  const [createWorkflow] = useMutation(CREATE_WORKFLOW, {
    onCompleted: () => refetch(),
  })
  const [updateWorkflow] = useMutation(UPDATE_WORKFLOW, {
    onCompleted: () => refetch(),
  })
  const [deleteWorkflow] = useMutation(DELETE_WORKFLOW, {
    onCompleted: () => refetch(),
  })

  const [createStage] = useMutation(CREATE_STAGE, {
    onCompleted: () => refetch(),
  })
  const [updateStage] = useMutation(UPDATE_STAGE, {
    onCompleted: () => refetch(),
  })
  const [deleteStage] = useMutation(DELETE_STAGE, {
    onCompleted: () => refetch(),
  })

  const workflows: Workflow[] = workflowsData?.workflows || []
  const roles: Role[] = rolesData?.userRoles || []

  const handleEditWorkflow = (wf: Workflow) => {
    setEditingId(wf.id)
    setEditingWorkflow({ name: wf.name, active: wf.active })
  }

  const handleSaveWorkflow = async () => {
    try {
      if (editingId) {
        await updateWorkflow({
          variables: { id: editingId, input: editingWorkflow },
        })
        toast.success('Workflow updated successfully')
      } else {
        await createWorkflow({ variables: { input: editingWorkflow } })
        toast.success('Workflow created successfully')
      }
      setShowSuccess(true)
      setEditingId(null)
      setEditingWorkflow({})
      setIsAdding(false)
    } catch (err: any) {
      toast.error(err.message || 'Failed to save workflow')
    }
  }

  const handleDeleteWorkflow = async (id: number) => {
    if (!window.confirm('Delete this workflow?')) return
    try {
      await deleteWorkflow({ variables: { id } })
      toast.success('Workflow deleted successfully')
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete workflow')
    }
  }

  const handleEditStage = (stage: ApprovalStage) => {
    setEditingStageId(stage.id)
    setEditingStage({
      approver_role: stage.approver_role,
      order_number: stage.order_number,
      is_payment_stage: stage.is_payment_stage,
    })
  }

  const handleSaveStage = async (workflowId: number) => {
    try {
      if (editingStageId) {
        await updateStage({
          variables: {
            id: editingStageId,
            input: {
              order_number: editingStage.order_number,
              is_payment_stage: editingStage.is_payment_stage,
              workflow: { connect: { id: workflowId } },
              approver_role: {
                connect: { id: editingStage.approver_role?.id },
              },
            },
          },
        })
        toast.success('Stage updated successfully')
      } else {
        await createStage({
          variables: {
            input: {
              order_number: editingStage.order_number,
              is_payment_stage: editingStage.is_payment_stage,
              workflow: { connect: { id: workflowId } },
              approver_role: {
                connect: { id: editingStage.approver_role?.id },
              },
            },
          },
        })
        toast.success('Stage created successfully')
      }
      setShowSuccess(true)
      setEditingStageId(null)
      setEditingStage({})
      setIsAddingStage(false)
    } catch (err: any) {
      toast.error(err.message || 'Failed to save stage')
    }
  }

  const handleDeleteStage = async (id: number) => {
    if (!window.confirm('Delete this stage?')) return
    try {
      await deleteStage({ variables: { id } })
      toast.success('Stage deleted successfully')
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete stage')
    }
  }

  if (workflowsLoading || rolesLoading) {
    return <Loader minHeight={400} />
  }

  return (
    <Box>
      <PageHeader title="Workflows Management" icon={AccountTreeIcon} />

      <Box mb={2}>
        <IconButton
          color="primary"
          onClick={() => {
            setIsAdding(true)
            setEditingId(null)
            setEditingWorkflow({ name: '', active: true })
          }}
        >
          <AddCircleIcon color="success" />
        </IconButton>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Name</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isAdding && (
              <TableRow>
                <TableCell />
                <TableCell>
                  <TextField
                    size="small"
                    value={editingWorkflow.name || ''}
                    onChange={(e) =>
                      setEditingWorkflow({
                        ...editingWorkflow,
                        name: e.target.value,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    checked={editingWorkflow.active ?? true}
                    onChange={(e) =>
                      setEditingWorkflow({
                        ...editingWorkflow,
                        active: e.target.checked,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={handleSaveWorkflow} color="primary">
                    <SaveIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => setIsAdding(false)}
                    color="secondary"
                  >
                    <CancelIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            )}
            {workflows.map((wf) => (
              <React.Fragment key={wf.id}>
                <TableRow>
                  <TableCell>
                    <IconButton
                      onClick={() =>
                        setExpanded(expanded === wf.id ? null : wf.id)
                      }
                    >
                      {expanded === wf.id ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    {editingId === wf.id ? (
                      <TextField
                        size="small"
                        value={editingWorkflow.name || ''}
                        onChange={(e) =>
                          setEditingWorkflow({
                            ...editingWorkflow,
                            name: e.target.value,
                          })
                        }
                      />
                    ) : (
                      wf.name
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === wf.id ? (
                      <Checkbox
                        checked={editingWorkflow.active ?? false}
                        onChange={(e) =>
                          setEditingWorkflow({
                            ...editingWorkflow,
                            active: e.target.checked,
                          })
                        }
                      />
                    ) : wf.active ? (
                      'Yes'
                    ) : (
                      'No'
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === wf.id ? (
                      <>
                        <IconButton
                          onClick={handleSaveWorkflow}
                          color="primary"
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => setEditingId(null)}
                          color="secondary"
                        >
                          <CancelIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton onClick={() => handleEditWorkflow(wf)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteWorkflow(wf.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} style={{ padding: 0 }}>
                    <Collapse
                      in={expanded === wf.id}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box m={2}>
                        <Typography variant="h6">Approval Stages</Typography>
                        <IconButton
                          onClick={() => {
                            setIsAddingStage(true)
                            setEditingStageId(null)
                            setEditingStage({
                              order_number: 1,
                              is_payment_stage: false,
                            })
                          }}
                        >
                          <AddCircleIcon color="success" />
                        </IconButton>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Order</TableCell>
                              <TableCell>Role</TableCell>
                              <TableCell>Payment Stage</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {isAddingStage && (
                              <TableRow>
                                <TableCell>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={editingStage.order_number || ''}
                                    onChange={(e) =>
                                      setEditingStage({
                                        ...editingStage,
                                        order_number: Number(e.target.value),
                                      })
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <FormControl
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                  >
                                    <Select
                                      value={
                                        editingStage.approver_role?.id || ''
                                      }
                                      onChange={(e) => {
                                        const role = roles.find(
                                          (r) => r.id === Number(e.target.value)
                                        )
                                        setEditingStage({
                                          ...editingStage,
                                          approver_role: role,
                                        })
                                      }}
                                    >
                                      {roles.map((r) => (
                                        <MenuItem key={r.id} value={r.id}>
                                          {r.id +
                                            ' - ' +
                                            r.name +
                                            ' (' +
                                            r.scope +
                                            ')'}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={
                                      editingStage.is_payment_stage ?? false
                                    }
                                    onChange={(e) =>
                                      setEditingStage({
                                        ...editingStage,
                                        is_payment_stage: e.target.checked,
                                      })
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    onClick={() => handleSaveStage(wf.id)}
                                    color="primary"
                                  >
                                    <SaveIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => setIsAddingStage(false)}
                                    color="secondary"
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            )}
                            {wf.approval_stages.map((st) => (
                              <TableRow key={st.id}>
                                <TableCell>
                                  {editingStageId === st.id ? (
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={editingStage.order_number || ''}
                                      onChange={(e) =>
                                        setEditingStage({
                                          ...editingStage,
                                          order_number: Number(e.target.value),
                                        })
                                      }
                                    />
                                  ) : (
                                    st.order_number
                                  )}
                                </TableCell>
                                <TableCell>
                                  {editingStageId === st.id ? (
                                    <FormControl
                                      size="small"
                                      sx={{ minWidth: 150 }}
                                    >
                                      <Select
                                        value={
                                          editingStage.approver_role?.id || ''
                                        }
                                        onChange={(e) => {
                                          const role = roles.find(
                                            (r) =>
                                              r.id === Number(e.target.value)
                                          )
                                          setEditingStage({
                                            ...editingStage,
                                            approver_role: role,
                                          })
                                        }}
                                      >
                                        {roles.map((r) => (
                                          <MenuItem key={r.id} value={r.id}>
                                            {r.name + ' (' + r.scope + ')'}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    st.approver_role?.id +
                                      ' - ' +
                                      st.approver_role?.name +
                                      ' (' +
                                      st.approver_role?.scope +
                                      ')' || '-'
                                  )}
                                </TableCell>
                                <TableCell>
                                  {editingStageId === st.id ? (
                                    <Checkbox
                                      checked={
                                        editingStage.is_payment_stage ?? false
                                      }
                                      onChange={(e) =>
                                        setEditingStage({
                                          ...editingStage,
                                          is_payment_stage: e.target.checked,
                                        })
                                      }
                                    />
                                  ) : st.is_payment_stage ? (
                                    'Yes'
                                  ) : (
                                    'No'
                                  )}
                                </TableCell>
                                <TableCell>
                                  {editingStageId === st.id ? (
                                    <>
                                      <IconButton
                                        onClick={() => handleSaveStage(wf.id)}
                                        color="primary"
                                      >
                                        <SaveIcon />
                                      </IconButton>
                                      <IconButton
                                        onClick={() => setEditingStageId(null)}
                                        color="secondary"
                                      >
                                        <CancelIcon />
                                      </IconButton>
                                    </>
                                  ) : (
                                    <>
                                      <IconButton
                                        onClick={() => handleEditStage(st)}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                      <IconButton
                                        onClick={() => handleDeleteStage(st.id)}
                                        color="error"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>
    </Box>
  )
}

export default Workflows
